import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Loader2, X, Eye, EyeOff, Type, FileText, Image, Search, Save } from "lucide-react";
import { getArticles, createArticle, updateArticle, deleteArticle } from "@/lib/api";

export const ArticlesCMS = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState("contenu");
  const [preview, setPreview] = useState(false);
  const textareaRef = useRef(null);

  const [formData, setFormData] = useState({
    titre: "",
    contenu: "",
    extrait: "",
    image: "",
    meta_title: "",
    meta_description: "",
    publie: false,
  });

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [formData.contenu, showModal]);

  const loadData = async () => {
    try {
      const res = await getArticles();
      setArticles(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      if (editing) {
        await updateArticle(editing.id, formData);
        toast.success("Article mis à jour !");
      } else {
        await createArticle(formData);
        toast.success("Article créé !");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (article) => {
    setEditing(article);
    setFormData({
      titre: article.titre,
      contenu: article.contenu,
      extrait: article.extrait,
      image: article.image || "",
      meta_title: article.meta_title || "",
      meta_description: article.meta_description || "",
      publie: article.publie,
    });
    setActiveTab("contenu");
    setPreview(false);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await deleteArticle(id);
      toast.success("Article supprimé !");
      loadData();
    } catch {
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ titre: "", contenu: "", extrait: "", image: "", meta_title: "", meta_description: "", publie: false });
    setActiveTab("contenu");
    setPreview(false);
  };

  const isHtml = formData.contenu.trim().startsWith("<");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nouvel article
        </button>
      </div>

      {articles.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left p-4 font-semibold text-[#004333]">Titre</th>
                <th className="text-left p-4 font-semibold text-[#004333] hidden sm:table-cell">Statut</th>
                <th className="text-right p-4 font-semibold text-[#004333]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4">
                    <p className="font-medium text-[#004333]">{article.titre}</p>
                    {article.extrait && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{article.extrait}</p>}
                  </td>
                  <td className="p-4 hidden sm:table-cell">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${article.publie ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {article.publie ? "Publié" : "Brouillon"}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => handleEdit(article)} className="p-2 text-gray-400 hover:text-[#004333] transition-colors"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(article.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-gray-500">
          Aucun article. Créez votre premier article de blog.
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-[#F5F0EB] flex flex-col overflow-hidden">

          {/* Top bar */}
          <div className="flex-shrink-0 flex items-center justify-between gap-4 px-6 py-3 bg-white border-b border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <FileText className="w-5 h-5 text-[#C9922A] flex-shrink-0" />
              <input
                type="text"
                required
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                placeholder="Titre de l'article..."
                className="text-lg font-semibold text-[#004333] bg-transparent border-none outline-none w-full placeholder-gray-300"
              />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <span className="text-sm text-gray-500 hidden sm:inline">{formData.publie ? "Publié" : "Brouillon"}</span>
                <button
                  type="button"
                  onClick={() => setFormData(f => ({ ...f, publie: !f.publie }))}
                  className={`relative w-10 h-5 rounded-full transition-colors ${formData.publie ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.publie ? "translate-x-5" : ""}`} />
                </button>
              </label>
              <button
                type="button"
                onClick={handleSubmit}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#004333] text-white text-sm font-semibold rounded-lg hover:bg-[#005544] transition-colors"
              >
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">{editing ? "Mettre à jour" : "Créer"}</span>
              </button>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex-shrink-0 flex items-center px-6 bg-white border-b border-gray-200">
            {[
              { id: "contenu", icon: Type, label: "Contenu" },
              { id: "extrait", icon: FileText, label: "Extrait" },
              { id: "media", icon: Image, label: "Image" },
              { id: "seo", icon: Search, label: "SEO" },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => { setActiveTab(tab.id); setPreview(false); }}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id ? "border-[#C9922A] text-[#C9922A]" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto px-6 py-6">

              {activeTab === "contenu" && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Contenu</label>
                      {isHtml && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">HTML</span>}
                    </div>
                    {isHtml && (
                      <button
                        type="button"
                        onClick={() => setPreview(p => !p)}
                        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#004333] border border-gray-200 rounded-lg px-3 py-1.5 transition-colors"
                      >
                        {preview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {preview ? "Éditer" : "Aperçu"}
                      </button>
                    )}
                  </div>

                  {preview && isHtml ? (
                    <div
                      className="min-h-[60vh] bg-white rounded-xl border border-gray-200 p-8 article-html-content"
                      dangerouslySetInnerHTML={{ __html: formData.contenu }}
                    />
                  ) : (
                    <textarea
                      ref={textareaRef}
                      required
                      value={formData.contenu}
                      onChange={(e) => setFormData({ ...formData, contenu: e.target.value })}
                      placeholder={"Saisissez le contenu...\n\nHTML accepté :\n<h2>Titre</h2>\n<p>Paragraphe</p>"}
                      className="w-full min-h-[60vh] bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-700 font-mono leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 focus:border-[#C9922A]/50 transition-colors"
                    />
                  )}
                  <p className="mt-2 text-xs text-gray-400">
                    {formData.contenu.length} caractères
                    {isHtml ? " · HTML détecté" : " · Commencez par < pour le mode HTML"}
                  </p>
                </div>
              )}

              {activeTab === "extrait" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Extrait <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-400 font-normal ml-2">Résumé affiché dans la liste</span>
                  </label>
                  <textarea
                    required
                    rows={8}
                    value={formData.extrait}
                    onChange={(e) => setFormData({ ...formData, extrait: e.target.value })}
                    placeholder="Résumé court et accrocheur (2-3 phrases)..."
                    className="w-full bg-white border border-gray-200 rounded-xl p-5 text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 focus:border-[#C9922A]/50 transition-colors"
                  />
                  <p className="mt-2 text-xs text-gray-400">{formData.extrait.length} / 300 caractères</p>
                </div>
              )}

              {activeTab === "media" && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image principale</label>
                    <input
                      type="text"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      placeholder="/images/photo-xxxx.jpg"
                      className="w-full bg-white border border-gray-200 rounded-xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 transition-colors"
                    />
                  </div>
                  {formData.image && (
                    <img src={formData.image} alt="" className="w-full max-h-64 object-cover rounded-xl border border-gray-200" onError={(e) => { e.target.style.display = "none"; }} />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">Sélectionner une image locale</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        "/images/photo-1669300568006.jpg", "/images/photo-1659027793165.jpg",
                        "/images/photo-1760372060494.jpg", "/images/pexels-6782578.jpg",
                        "/images/photo-1773847400278.jpg", "/images/photo-1757924461488.jpg",
                        "/images/photo-1600585154340.jpg", "/images/photo-1554224155.jpg",
                      ].map(src => (
                        <button key={src} type="button" onClick={() => setFormData(f => ({ ...f, image: src }))}
                          className={`relative rounded-lg overflow-hidden border-2 transition-all ${formData.image === src ? "border-[#C9922A]" : "border-transparent hover:border-gray-300"}`}>
                          <img src={src} alt="" className="w-full h-20 object-cover" />
                          {formData.image === src && (
                            <div className="absolute inset-0 bg-[#C9922A]/20 flex items-center justify-center">
                              <span className="w-5 h-5 bg-[#C9922A] rounded-full flex items-center justify-center text-white text-xs">✓</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "seo" && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Meta title <span className="text-xs text-gray-400 font-normal ml-1">60 car. max</span></label>
                    <input type="text" value={formData.meta_title} onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                      placeholder={formData.titre} className="w-full bg-white border border-gray-200 rounded-xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 transition-colors" />
                    <p className="mt-1 text-xs text-gray-400">{formData.meta_title.length} / 60</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Meta description <span className="text-xs text-gray-400 font-normal ml-1">160 car. max</span></label>
                    <textarea rows={4} value={formData.meta_description} onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      placeholder={formData.extrait} className="w-full bg-white border border-gray-200 rounded-xl p-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 transition-colors" />
                    <p className="mt-1 text-xs text-gray-400">{formData.meta_description.length} / 160</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Aperçu Google</p>
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                      <p className="text-blue-700 text-base font-medium truncate">{formData.meta_title || formData.titre || "Titre"}</p>
                      <p className="text-green-700 text-xs my-0.5">https://happyhouse-conciergerie.fr/blog/...</p>
                      <p className="text-gray-600 text-sm line-clamp-2">{formData.meta_description || formData.extrait || "Description..."}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlesCMS;
